import { Box, CircularProgress, Drawer, IconButton, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderIcon from '@mui/icons-material/Folder';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useState } from 'react';
import { mindtimeBackupsApi } from 'src/api/mindtime/backupsApi';
import logger from 'src/utils/logger';
import BackupsetDetailRow from 'src/mindtime/components/backups/shared/BackupsetDetailRow';
import BackupsetSectionCard from 'src/mindtime/components/backups/shared/BackupsetSectionCard';
import { BackupJob, BackupJobApiItem, BackupsetDetails } from 'src/types/mindtime/backups';

interface BackupsetDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    backupsetDetails: BackupsetDetails | null;
    loading?: boolean;
    error?: string | null;
}

type TabValue = 'statistics' | 'backupSource' | 'settings';

const useStyles = makeStyles(() => ({
    drawerBackdrop: {
        backgroundColor: 'rgba(0, 0, 0, 0.4) !important',
        backdropFilter: 'blur(5px)',
        transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 300ms cubic-bezier(0.4, 0, 0.2, 1) !important',
    },
    drawerPaper: {
        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) !important',
    },
}));

const BackupsetDetailsDrawer = ({
    open,
    onClose,
    backupsetDetails,
    loading = false,
    error = null,
}: BackupsetDetailsDrawerProps) => {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState<TabValue>('statistics');
    const [jobsDays, setJobsDays] = useState<number>(7);
    const [backupJobs, setBackupJobs] = useState<BackupJob[] | null>(null);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [jobsError, setJobsError] = useState<string | null>(null);
    const [reportDownloadingJobId, setReportDownloadingJobId] = useState<string | null>(null);

    const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: TabValue) => {
        setActiveTab(newValue);
    };

    const formatBackupStart = (dateString: string): string => {
        if (!dateString || dateString === '-') return '-';
        try {
            // Handle format like "2026-02-19-22-00-00" or standard date strings
            if (dateString.includes('-') && dateString.split('-').length >= 6) {
                const parts = dateString.split('-');
                const year = parts[0];
                const month = parts[1];
                const day = parts[2];
                const hour = parts[3];
                const minute = parts[4];
                const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
                return date.toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                }) + ' ' + date.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                });
            }
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
            }) + ' ' + date.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
        } catch {
            return dateString;
        }
    };

    const getStatusBadge = (status: 'SUCCESS' | 'WARNING' | 'FAILED' | 'RUNNING') => {
        const isSuccess = status === 'SUCCESS';
        const isWarning = status === 'WARNING';
        const isRunning = status === 'RUNNING';
        const bg = isSuccess ? '#D1FAE5' : isWarning ? '#FEF3C7' : isRunning ? '#DBEAFE' : '#FEE2E2';
        const dotColor = isSuccess ? '#10B981' : isWarning ? '#F59E0B' : isRunning ? '#2563EB' : '#DC2626';
        const textColor = isSuccess ? '#047857' : isWarning ? '#B45309' : isRunning ? '#1D4ED8' : '#DC2626';
        const text = status;

        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.25,
                    borderRadius: '9999px',
                    bgcolor: bg,
                    fontSize: 10,
                    fontWeight: isWarning ? 600 : 700,
                }}
            >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor }} />
                <Typography
                    sx={{
                        fontSize: 10,
                        fontWeight: isWarning ? 600 : 700,
                        color: textColor,
                        lineHeight: '12px',
                    }}
                >
                    {text}
                </Typography>
            </Box>
        );
    };

    const mapBackupJobStatusToUi = (status: string): BackupJob['status'] => {
        if (!status) {
            return 'FAILED';
        }

        const normalized = status.toUpperCase();

        if (normalized === 'BS_STOP_SUCCESS') {
            return 'SUCCESS';
        }

        if (normalized === 'BS_BACKUP_NOT_FINISHED') {
            return 'RUNNING';
        }

        if (
            normalized.includes('WARNING') ||
            normalized.includes('PARTIAL') ||
            normalized.includes('PAUSE')
        ) {
            return 'WARNING';
        }

        return 'FAILED';
    };

    const formatBytesToReadable = (bytes: number): string => {
        if (!Number.isFinite(bytes) || bytes <= 0) {
            return '0 B';
        }

        const kb = bytes / 1024;
        if (kb < 1024) {
            return `${kb.toFixed(2)} KB`;
        }

        const mb = kb / 1024;
        if (mb < 1024) {
            return `${mb.toFixed(2)} MB`;
        }

        const gb = mb / 1024;
        return `${gb.toFixed(2)} GB`;
    };

    // Convert the raw backup job data from the API to the UI format
    const mapBackupJobsApiToUi = (items: BackupJobApiItem[]): BackupJob[] =>
        items.map((item) => {
            const backupStart = item.ID || item.StartTime || item.backupDate;
            const status = mapBackupJobStatusToUi(item.BackupJobStatus);
            const uploaded = formatBytesToReadable(item.UploadSize ?? 0);

            const totalNewBytes = Number(item.TotalNewFilesSize ?? 0);
            const totalUpdatedBytes = Number(item.TotalUpdatedFilesSize ?? 0);
            const totalDeletedBytes = Number(item.TotalDeletedFilesSize ?? 0);

            const totalNew =
                Number.isFinite(totalNewBytes) && totalNewBytes > 0
                    ? formatBytesToReadable(totalNewBytes)
                    : totalNewBytes === 0
                        ? '0 B'
                        : '-';

            const totalUpdated =
                Number.isFinite(totalUpdatedBytes) && totalUpdatedBytes > 0
                    ? formatBytesToReadable(totalUpdatedBytes)
                    : totalUpdatedBytes === 0
                        ? '0 B'
                        : '-';

            const totalDeleted =
                Number.isFinite(totalDeletedBytes) && totalDeletedBytes > 0
                    ? formatBytesToReadable(totalDeletedBytes)
                    : totalDeletedBytes === 0
                        ? '0 B'
                        : '-';

            const backupJobId = item.ID || undefined;

            return {
                backupStart,
                status,
                uploaded,
                TotalNewFilesSize: totalNew,
                TotalUpdatedFilesSize: totalUpdated,
                TotalDeletedFilesSize: totalDeleted,
                backupJobId,
            };
        });

    const fetchBackupJobsList = () => {
        if (!backupsetDetails?.backupSetId) {
            return;
        }

        setJobsLoading(true);
        setJobsError(null);

        mindtimeBackupsApi
            .getBackupJobs(backupsetDetails.backupSetId, jobsDays)
            .then((rawJobs) => {
                const mapped = mapBackupJobsApiToUi(rawJobs);
                setBackupJobs(mapped);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch backup jobs', err);
                setJobsError('Failed to load backup jobs');
                setBackupJobs(null);
            })
            .finally(() => {
                setJobsLoading(false);
            });
    };

    const handleDownloadBackupJobReport = (backupJobId: string) => {
        setReportDownloadingJobId(backupJobId);
        mindtimeBackupsApi
            .downloadBackupJobReport(backupJobId)
            .then(({ blob, filename }) => {
                const name =
                    filename && filename.trim().length > 0
                        ? filename
                        : `backup-job-report-${backupJobId}.pdf`;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', name);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
                logger(err, 'error');
                // eslint-disable-next-line no-console
                console.error('Failed to download backup job report', err);
            })
            .finally(() => {
                setReportDownloadingJobId(null);
            });
    };

    const refreshBackupJobsList = () => {
        if (!backupsetDetails?.backupSetId) {
            return;
        }

        const { backupSetId } = backupsetDetails;

        setJobsLoading(true);
        setJobsError(null);

        mindtimeBackupsApi
            .exportBackupJobReportsCurrentDay(backupSetId)
            .then(() =>
                mindtimeBackupsApi
                    .refreshBackupJobs(backupSetId, jobsDays)
                    .then((rawJobs) => {
                        const mapped = mapBackupJobsApiToUi(rawJobs);
                        setBackupJobs(mapped);
                    })
                    .catch((err) => {
                        logger(err, 'error');
                        // eslint-disable-next-line no-console
                        console.error('Failed to refresh backup jobs', err);
                        setJobsError('Failed to refresh backup jobs');
                        setBackupJobs(null);
                    })
            )
            .catch((err) => {
                logger(err, 'error');
                // eslint-disable-next-line no-console
                console.error('Failed to export backup job reports', err);
                setJobsError('Failed to export backup job reports');
            })
            .finally(() => {
                setJobsLoading(false);
            });
    };

    useEffect(() => {
        if (!open || !backupsetDetails?.backupSetId) {
            return;
        }

        fetchBackupJobsList();
    }, [open, backupsetDetails?.backupSetId, jobsDays]);

    const renderStatisticsTab = () => {
        const { statistics } = backupsetDetails;

        const rows: Array<{
            label: string;
            value: React.ReactNode;
            bg: string;
            highlighted?: boolean;
            multiline?: boolean;
        }> = [
                { label: 'Last Login', value: statistics.lastLogin, bg: '#F3F3F3' },
                { label: 'Run on computer', value: statistics.runOnComputer, bg: '#E5E5E5' },
                { label: 'Scheduled', value: statistics.scheduled, bg: 'transparent', multiline: true },
                {
                    label: 'Backup Login Online',
                    value: statistics.backupLoginOnline ? 'Online' : 'Offline',
                    bg: '#555555',
                    highlighted: true,
                },
                { label: 'Deduplication Saving', value: statistics.deduplicationSaving, bg: '#F3F3F3' },
                { label: 'Data', value: statistics.data, bg: 'transparent' },
                { label: 'Retention', value: statistics.retention, bg: '#F3F3F3' },
                { label: 'Recycle Bin', value: statistics.recycleBin, bg: 'transparent' },
                { label: 'Last restore job', value: statistics.LastRestoreTime, bg: '#F3F3F3' },
                { label: 'Last Data Integrity Check', value: statistics.lastDataIntegrityCheck, bg: 'transparent' },
            ];

        if (statistics.files) {
            rows.splice(7, 0, {
                label: 'Files',
                value: statistics.files,
                bg: '#F3F3F3',
            });
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {rows.map((row) => (
                    <BackupsetDetailRow
                        key={row.label}
                        label={row.label}
                        value={row.value}
                        backgroundColor={row.bg}
                        highlighted={row.highlighted}
                        multiline={row.multiline}
                    />
                ))}
            </Box>
        );
    };

    const renderBackupSourceTab = () => {
        const included = backupsetDetails.backupSource?.included || [];
        const excluded = backupsetDetails.backupSource?.excluded || [];

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <BackupsetSectionCard title="Backup Source" items={included} />
                <BackupsetSectionCard
                    title="Exclude The Following Files/Directories"
                    items={excluded}
                />
            </Box>
        );
    };

    const renderSettingsTab = () => {
        const { settings } = backupsetDetails;

        if (!settings) {
            return (
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ color: '#64748B', fontSize: 14 }}>
                        Settings data not available for this backup set.
                    </Typography>
                </Box>
            );
        }

        const enabledLabel = (value?: boolean) => {
            if (value == null) {
                return '-';
            }
            return value ? 'Enabled' : 'Disabled';
        };

        const enabledColor = (value?: boolean) => {
            if (value == null) {
                return undefined;
            }
            return value ? '#16A34A' : '#DC2626';
        };

        const ipRanges =
            settings.allowedIps && settings.allowedIps.length > 0
                ? settings.allowedIps
                    .map((range) => {
                        if (range.startIp && range.endIp) {
                            return `${range.startIp} - ${range.endIp}`;
                        }
                        return range.startIp || range.endIp || '';
                    })
                    .filter(Boolean)
                    .join(', ')
                : '-';

        const rows: Array<{
            label: string;
            value: React.ReactNode;
            bg: string;
            valueColor?: string;
        }> = [
                {
                    label: 'Temporary directory',
                    value: settings.workingDir || '-',
                    bg: '#F3F3F3',
                },
                {
                    label: 'Deduplication',
                    value: enabledLabel(settings.dedupeEnabled),
                    bg: 'transparent',
                    valueColor: enabledColor(settings.dedupeEnabled),
                },
                {
                    label: 'Deduplication scope',
                    value: settings.dedupeScope || '-',
                    bg: '#F3F3F3',
                },
                {
                    label: 'Deduplication block',
                    value: settings.dedupeBlockSize || '-',
                    bg: 'transparent',
                },
                {
                    label: 'Follow links',
                    value: enabledLabel(settings.followLinks),
                    bg: '#F3F3F3',
                    valueColor: enabledColor(settings.followLinks),
                },
                {
                    label: 'Compression',
                    value: settings.compression || '-',
                    bg: 'transparent',
                },
                {
                    label: 'Restore Drill',
                    value: enabledLabel(settings.restoreDrill),
                    bg: '#F3F3F3',
                    valueColor: enabledColor(settings.restoreDrill),
                },
                {
                    label: 'File Viewer',
                    value: enabledLabel(settings.fileViewer),
                    bg: 'transparent',
                    valueColor: enabledColor(settings.fileViewer),
                },
                {
                    label: 'IP allowed for restore',
                    value: ipRanges,
                    bg: '#F3F3F3',
                },
            ];

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {rows.map((row) => (
                    <BackupsetDetailRow
                        key={row.label}
                        label={row.label}
                        value={row.value}
                        backgroundColor={row.bg}
                        highlighted={false}
                        valueColor={row.valueColor}
                    />
                ))}
            </Box>
        );
    };

    const renderBackupJobsTable = () => {
        const fallbackJobs = backupsetDetails.backupJobs || [];
        const effectiveJobs = (backupJobs && backupJobs.length > 0 ? backupJobs : fallbackJobs) || [];

        return (
            <Box
                sx={{
                    mt: 3,
                    mb: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#FFFFFF',
                    overflow: 'hidden',
                }}
            >
                {/* Backup Jobs Header */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: '12px 16px',
                        gap: '8px',
                        borderBottom: '1px solid #F1F5F9',
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={refreshBackupJobsList}
                        disabled={jobsLoading || !backupsetDetails?.backupSetId}
                        sx={{
                            p: 0.5,
                            color: '#1BB4EB',
                            '&:hover': {
                                backgroundColor: 'rgba(27, 180, 235, 0.1)',
                            },
                        }}
                        title="Refresh Backup Jobs"
                    >
                        <RefreshIcon sx={{ fontSize: 20, color: '#1BB4EB' }} />
                    </IconButton>
                    <Typography
                        sx={{
                            fontFamily: 'Inter',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '21px',
                            color: '#0F172A',
                        }}
                    >
                        Backup Jobs
                    </Typography>
                    <Box
                        sx={{
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontSize: '12px',
                                color: '#64748B',
                            }}
                        >
                            Last
                        </Typography>
                        <TextField
                            type="number"
                            size="small"
                            value={jobsDays}
                            onChange={(event) => {
                                const next = Number(event.target.value);
                                if (!Number.isFinite(next)) {
                                    return;
                                }
                                const clamped = Math.min(Math.max(next, 1), 365);
                                setJobsDays(clamped);
                            }}
                            InputProps={{
                                inputProps: {
                                    min: 1,
                                    max: 365,
                                },
                            }}
                            sx={{
                                width: 80,
                                '& input': {
                                    fontSize: 12,
                                    padding: '6px 8px',
                                },
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontSize: '12px',
                                color: '#64748B',
                            }}
                        >
                            days
                        </Typography>
                    </Box>
                </Box>

                {/* Table */}
                <Box
                    sx={{
                        width: '100%',
                        overflowX: 'auto',
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        lineHeight: '12px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        color: '#64748B',
                                        padding: '12px 24px',
                                    }}
                                >
                                    BACKUP START
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 700,
                                        fontSize: '10px',
                                        lineHeight: '12px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        color: '#64748B',
                                        padding: '12px 24px',
                                    }}
                                >
                                    STATUS
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        lineHeight: '12px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        color: '#64748B',
                                        padding: '12px 24px',
                                    }}
                                >
                                    UPLOADED
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        lineHeight: '12px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        color: '#64748B',
                                        padding: '12px 24px',
                                    }}
                                >
                                    NEW
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        lineHeight: '12px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        color: '#64748B',
                                        padding: '12px 24px',
                                    }}
                                >
                                    CHANGED
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        lineHeight: '12px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        color: '#64748B',
                                        padding: '12px 24px',
                                    }}
                                >
                                    DELETED
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        lineHeight: '12px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        color: '#64748B',
                                        padding: '12px 24px',
                                    }}
                                >
                                    REPORT
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobsLoading && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 1,
                                                py: 2,
                                            }}
                                        >
                                            <CircularProgress size={20} sx={{ color: '#0EA5E9' }} />
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Inter',
                                                    fontSize: '12px',
                                                    color: '#64748B',
                                                }}
                                            >
                                                Loading backup jobs...
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!jobsLoading && jobsError && effectiveJobs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontSize: '12px',
                                                color: '#DC2626',
                                                py: 2,
                                            }}
                                        >
                                            {jobsError}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!jobsLoading && !jobsError && effectiveJobs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontSize: '12px',
                                                color: '#64748B',
                                                py: 2,
                                            }}
                                        >
                                            No backup jobs found for the selected period.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!jobsLoading &&
                                effectiveJobs.length > 0 &&
                                effectiveJobs.map((job) => (
                                    <TableRow
                                        key={job.backupJobId ?? `${job.backupStart}-${job.status}`}
                                    >
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontWeight: 600,
                                                fontSize: '12px',
                                                lineHeight: '15px',
                                                color: '#0F172A',
                                                padding: '16.5px 24px',
                                            }}
                                        >
                                            {formatBackupStart(job.backupStart)}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                padding: '16.5px 24px 16.5px 0px',
                                            }}
                                        >
                                            {getStatusBadge(job.status)}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontWeight: 400,
                                                fontSize: '12px',
                                                lineHeight: '15px',
                                                color: '#425466',
                                                padding: '16.5px 24px',
                                            }}
                                        >
                                            {job.uploaded}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontWeight: 400,
                                                fontSize: '12px',
                                                lineHeight: '15px',
                                                color: '#425466',
                                                padding: '17px 24px',
                                            }}
                                        >
                                            {job?.TotalNewFilesSize}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontWeight: 400,
                                                fontSize: '12px',
                                                lineHeight: '15px',
                                                color: '#425466',
                                                padding: '17px 24px',
                                            }}
                                        >
                                            {job.TotalUpdatedFilesSize}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontWeight: 400,
                                                fontSize: '12px',
                                                lineHeight: '15px',
                                                color: '#425466',
                                                padding: '17px 24px 16.5px',
                                            }}
                                        >
                                            {job?.TotalDeletedFilesSize}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                padding: '16.5px 24px 17px 0px',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !job.backupJobId ||
                                                    reportDownloadingJobId === job.backupJobId
                                                }
                                                onClick={() => {
                                                    if (job.backupJobId) {
                                                        handleDownloadBackupJobReport(job.backupJobId);
                                                    }
                                                }}
                                                title="Download report"
                                                sx={{
                                                    p: 0.5,
                                                    color: job.backupJobId ? '#1BB4EB' : '#94A3B8',
                                                    '&:hover': {
                                                        backgroundColor: job.backupJobId
                                                            ? 'rgba(27, 180, 235, 0.1)'
                                                            : 'transparent',
                                                    },
                                                }}
                                            >
                                                {reportDownloadingJobId === job.backupJobId ? (
                                                    <CircularProgress size={18} sx={{ color: '#1BB4EB' }} />
                                                ) : (
                                                    <DescriptionIcon
                                                        sx={{
                                                            fontSize: 20,
                                                            color: 'inherit',
                                                        }}
                                                    />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        );
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            transitionDuration={{ enter: 300, exit: 250 }}
            ModalProps={{
                BackdropProps: {
                    className: classes.drawerBackdrop,
                },
            }}
            PaperProps={{
                className: classes.drawerPaper,
                sx: {
                    width: { xs: '100%', sm: 857 },
                    maxWidth: '90vw',
                    height: '100vh',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0px 0px 1px rgba(12, 26, 75, 0.24), 0px 3px 8px -1px rgba(50, 50, 71, 0.05)',
                    // borderRadius: '16px',
                    borderTopLeftRadius: '16px',
                    borderBottomLeftRadius: '16px',
                },
            }}
            SlideProps={{
                timeout: { enter: 300, exit: 250 },
                easing: { enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '28px 30px',
                    gap: '10px',
                    height: '100%',
                    overflowY: 'hidden',
                }}
            >
                {/* Loading State */}
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            gap: 2,
                        }}
                    >
                        <CircularProgress size={48} sx={{ color: '#0EA5E9' }} />
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#64748B',
                            }}
                        >
                            Loading backupset details...
                        </Typography>
                    </Box>
                )}

                {/* Error State (when no data) */}
                {!loading && error && !backupsetDetails && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            gap: 2,
                        }}
                    >
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: '#DC2626' }} />
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#DC2626',
                                textAlign: 'center',
                            }}
                        >
                            {error}
                        </Typography>
                    </Box>
                )}

                {/* Content (when not loading and data is available) */}
                {!loading && backupsetDetails && (
                    <>
                        {/* Header Section */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                gap: '20px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                    gap: '10px',
                                    flex: 1,
                                }}
                            >
                                <FolderIcon sx={{ fontSize: 24, color: '#94A3B8', width: 26, height: 26 }} />
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        lineHeight: '25px',
                                        color: '#16192C',
                                    }}
                                >
                                    {backupsetDetails.name}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'Figtree',
                                        fontStyle: 'italic',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '150%',
                                        color: '#16192C',
                                        opacity: 0.3,
                                    }}
                                >
                                    Backupset ID: {backupsetDetails.backupSetId}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={onClose}
                                sx={{
                                    padding: '5px 10px',
                                    width: 34,
                                    height: 24,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                x
                            </IconButton>
                        </Box>

                        {/* Divider */}
                        <Box
                            sx={{
                                width: '100%',
                                height: 0,
                                border: '1px solid #EDF2F7',
                            }}
                        />

                        {/* Storage Info Section */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                            }}
                        >
                            <CloudQueueIcon sx={{ fontSize: 16, color: '#425466', width: 16, height: 16 }} />
                            <Typography
                                sx={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    lineHeight: '15px',
                                    color: '#425466',
                                }}
                            >
                                {backupsetDetails.storageUsed} ||
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'italic',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    lineHeight: '15px',
                                    color: '#425466',
                                    opacity: 0.5,
                                }}
                            >
                                Retention: {backupsetDetails.retentionDays} Days
                            </Typography>
                        </Box>

                        {/* Divider */}
                        <Box
                            sx={{
                                width: '100%',
                                height: 0,
                                border: '1px solid #EDF2F7',
                            }}
                        />

                        {/* Tabs Navigation */}
                        <Box sx={{ width: '100%', mt: 1 }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                sx={{
                                    minHeight: 'auto',
                                    '& .MuiTabs-indicator': {
                                        height: 2,
                                        backgroundColor: '#1BB4EB',
                                    },
                                }}
                            >
                                <Tab
                                    label="Statistics"
                                    value="statistics"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        fontFeatureSettings: "'salt' on",
                                        color: activeTab === 'statistics' ? '#1A202C' : '#718096',
                                        textTransform: 'none',
                                        minWidth: 'auto',
                                        padding: '0 15px',
                                        marginRight: '30px',
                                    }}
                                />
                                <Tab
                                    label="Backup Source"
                                    value="backupSource"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        fontFeatureSettings: "'salt' on",
                                        color: activeTab === 'backupSource' ? '#1A202C' : '#718096',
                                        textTransform: 'none',
                                        minWidth: 'auto',
                                        padding: '0 15px',
                                        marginRight: '30px',
                                    }}
                                />
                                <Tab
                                    label="Settings"
                                    value="settings"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        fontFeatureSettings: "'salt' on",
                                        color: activeTab === 'settings' ? '#1A202C' : '#718096',
                                        textTransform: 'none',
                                        minWidth: 'auto',
                                        padding: '0 15px',
                                    }}
                                />
                            </Tabs>
                        </Box>

                        {/* Tab Content */}
                        <Box sx={{ width: '100%', mt: 2 }}>
                            {activeTab === 'statistics' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {renderStatisticsTab()}
                                </Box>
                            )}
                            {activeTab === 'backupSource' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {renderBackupSourceTab()}
                                </Box>
                            )}
                            {activeTab === 'settings' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {renderSettingsTab()}
                                </Box>
                            )}
                        </Box>

                        {/* Backup Jobs Section */}
                        {renderBackupJobsTable()}
                    </>
                )}
            </Box>
        </Drawer>
    );
};

export default BackupsetDetailsDrawer;
